import { debounce } from '@/lib/utils/debounce';
import { useRouter } from 'next/navigation';
import React, { useCallback, useEffect, useMemo, useState, useTransition } from 'react';
import { getGeolocationFromPlaceId, locationAutocompleteSearch } from '@/lib/server/geolocation/actions';
import type { Geolocation, LocationPrediction } from '@/lib/server/geolocation/types';
type UseLocationAutocompleteOptions = {
  controlledValue?: string;
  minQueryLength?: number;
  updateUrlParams?: boolean;
  urlParamNames?: {
    lat?: string;
    lng?: string;
    address?: string;
  };
  urlPath?: string;
  scrollToResults?: boolean;
  onLocationChange?: (location: Geolocation) => void;
};
export function useLocationAutocomplete({
  controlledValue,
  minQueryLength = 2,
  updateUrlParams = false,
  urlParamNames = {
    lat: 'lat',
    lng: 'lng',
    address: 'address'
  },
  urlPath = '/locations',
  scrollToResults = false,
  onLocationChange
}: UseLocationAutocompleteOptions) {
  const router = useRouter();
  const [isPending, startTransition] = useTransition();
  const [autocompleteResults, setAutocompleteResults] = useState<LocationPrediction[]>([]);
  const [locationQuery, setLocationQuery] = useState(controlledValue || '');
  const [isOpen, setIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Sync controlled value
  useEffect(() => {
    if (controlledValue !== undefined) {
      setLocationQuery(controlledValue);
    }
  }, [controlledValue]);

  // Debounced search function
  const debouncedSearch = useMemo(() => {
    return debounce((searchQuery: string) => {
      if (!searchQuery) {
        setAutocompleteResults([]);
        return;
      }
      startTransition(async () => {
        try {
          const res = await locationAutocompleteSearch({
            query: searchQuery
          });
          setAutocompleteResults(res.success ? res.value : []);
        } catch (error) {
          console.error('Error in autocomplete search:', error);
          setAutocompleteResults([]);
        }
      });
    }, 500);
  }, [startTransition]);
  useEffect(() => {
    if (locationQuery && locationQuery.length >= minQueryLength) {
      debouncedSearch(locationQuery);
    } else {
      setAutocompleteResults([]);
      if (!locationQuery) {
        setIsOpen(false);
      }
    }
    return () => {
      debouncedSearch.cancel?.();
    };
  }, [locationQuery, debouncedSearch, minQueryLength]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('[data-slot="command"]')) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);
  const handleSelectLocation = useCallback(async (location: Geolocation) => {
    setLocationQuery(location.formattedAddress || '');
    if (onLocationChange) {
      onLocationChange(location);
    }
    if (updateUrlParams) {
      const params = new URLSearchParams(window.location.search);
      params.set(urlParamNames.lat || 'lat', location.latitude.toString());
      params.set(urlParamNames.lng || 'lng', location.longitude.toString());
      params.set(urlParamNames.address || 'address', location.formattedAddress || '');
      const hash = scrollToResults ? '#search-results' : '';
      router.replace(`${urlPath}?${params.toString()}${hash}`);
    }
    if (scrollToResults) {
      setTimeout(() => {
        const element = document.getElementById('search-results');
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }, 100);
    }
  }, [updateUrlParams, urlParamNames, urlPath, scrollToResults, router, onLocationChange]);
  const onSelectAutocomplete = useCallback(async (prediction: LocationPrediction) => {
    setIsOpen(false);
    setAutocompleteResults([]);
    startTransition(async () => {
      try {
        const res_0 = await getGeolocationFromPlaceId(prediction.place_id);
        if (res_0.success && res_0.value) {
          await handleSelectLocation(res_0.value);
        }
      } catch (error_0) {
        console.error('Error selecting autocomplete result:', error_0);
      }
    });
  }, [handleSelectLocation, startTransition]);
  const handleLocationFound = useCallback((location_0: Geolocation) => {
    setErrorMessage(null);
    handleSelectLocation(location_0);
  }, [handleSelectLocation]);
  const handleLocationError = useCallback((error_1: string) => {
    setErrorMessage(error_1);
    setTimeout(() => setErrorMessage(null), 5000);
  }, []);
  const handleKeyDown = useCallback((e_0: React.KeyboardEvent<HTMLInputElement>) => {
    if (e_0.key === 'Enter' && autocompleteResults.length > 0) {
      e_0.preventDefault();
      onSelectAutocomplete(autocompleteResults[0]);
    }
  }, [autocompleteResults, onSelectAutocomplete]);
  const defaultRenderItem = (prediction_0: LocationPrediction) => <>
      {prediction_0.structured_formatting.main_text}
      {prediction_0.structured_formatting.secondary_text && <span className="text-muted-foreground ml-2">
          {prediction_0.structured_formatting.secondary_text}
        </span>}
    </>;
  return {
    isPending,
    autocompleteResults,
    locationQuery,
    setLocationQuery,
    isOpen,
    setIsOpen,
    errorMessage,
    handleKeyDown,
    handleLocationFound,
    handleLocationError,
    onSelectAutocomplete,
    defaultRenderItem
  };
}